import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { getLoggedInUserInfo } from 'src/app/state';
import * as CoreActions from 'src/app/state/actions/core.actions';
import { State } from 'src/app/state/app.state';
import { IdentityServiceService } from '../../system-configuration/shared/services/identity-service.service';
import { UserInfoDto } from '../../user/shared/models/userinfo.dto';
import { Tenant } from '../shared/models/tenant.model';
import TenantConfigurationConstants from '../shared/models/tenant_configuration.constants';
import { TenantOrganizationType } from '../shared/models/tenant_organization_type.enum';
import { TenantType } from '../shared/models/tenant_type.enum';
import { TenantValidationResponse } from '../shared/models/tenant_validation_response.model';
import { TenantValidationErrors } from '../shared/models/tenant_validations_errors.enum';
import { TenantService } from '../shared/services/tenant.service';
import { TranslationLoaderFacade } from '../../localization/translation-loader.facade';

@Component({
  selector: 'app-tenant-creation',
  templateUrl: './tenant-creation.component.html',
  styleUrls: ['./tenant-creation.component.scss']
})
export class TenantCreationComponent implements OnInit {
  /**
   * The form generation method will be called inside of the constructor
   * So the property can be denoted as definite
   */
  tenantCreationForm!: FormGroup;
  networkStatus: boolean = false;
  networkStatus$: Subscription = Subscription.EMPTY;
  loggedInUserInfo!: UserInfoDto;

  public databaseName: string = TenantConfigurationConstants.DatabaseNamePrefix;
  // tenant: Tenant;
  serverList: string[] = [];
  clientCodeAlreadyUsed: boolean = false;
  clientNameAlreadyUsed: boolean = false;
  solutionIdAlreadyUsed: boolean = false;
  defaultDomainNameAlreadyUsed: boolean = false;
  @Output() dirtyEmitter = new EventEmitter<boolean>(false);

  constructor(private tenantService: TenantService, private router: Router,
    private identityService: IdentityServiceService, private store: Store<State>, private translationLoaderFacade: TranslationLoaderFacade) {
    this.getServerNameList();
    this.generateSettingsForm();
  }


  ngOnInit() {
    this.checkNetworkStatusForSaveTenant();
    this.getLoggedUser();
    this.tenantCreationForm.controls.isMigratedClient.setValue(false);
  }

  // FormControl Getters
  get clientName() {
    return this.tenantCreationForm.get('clientName');
  }

  get clientCode() {
    return this.tenantCreationForm.get('clientCode');
  }

  get clientSolutionId() {
    return this.tenantCreationForm.get('clientSolutionId');
  }

  get domainName() {
    return this.tenantCreationForm.get('domainName');
  }

  get isMigratedClient() {
    return this.tenantCreationForm.get('MigratedClient');
  }

  /*
  * Generate tenant creation form controllers
  */
  generateSettingsForm() {
    this.databaseName = TenantConfigurationConstants.DatabaseNamePrefix;
    this.tenantCreationForm = new FormGroup({
      clientCode: new FormControl('', [
        Validators.required,
        Validators.maxLength(30)
      ]),
      clientName: new FormControl('', [
        Validators.required,
        Validators.maxLength(100)
      ]),
      domainName: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^(?!.* .*)(?:[a-z0-9][a-z0-9-]{0,61}[a-z0-9]\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/)
      ]),
      clientSolutionId: new FormControl('', [
        Validators.required,
        Validators.maxLength(50)
      ]),
      clientServerName: new FormControl(),
      isMigratedClient: new FormControl()
    });
  }

  /*
  * Get available server name list
  */
  getServerNameList() {
    this.tenantService.getDatabaseServers()
      .subscribe((response: string[]) => {
        this.serverList = response;
      },
        (err) => {
          this.handleError(err, '_Clients_Retrieving_Server_Names_Failed_Ts_Text_')
        });
  }

  /**
   * Triggers when saving the client data
   * @param event 
  //  */

  saveTenant() {
    if (!(this.tenantCreationForm.valid && this.networkStatus)) {
      return;
    }
    this.SubscribeToClientCreation();
    const tenantData = new Tenant();
    tenantData.TenantCode = this.tenantCreationForm.controls.clientCode.value;
    tenantData.TenantName = this.tenantCreationForm.controls.clientName.value.trim();
    tenantData.DomainName = this.tenantCreationForm.controls.domainName.value.trim();
    tenantData.DatabaseName = this.databaseName;
    tenantData.SolutionId = this.tenantCreationForm.controls.clientSolutionId.value;
    tenantData.Server = this.tenantCreationForm.controls.clientServerName.value ?
      this.tenantCreationForm.controls.clientServerName.value :
      this.serverList[0];

    tenantData.ClientType = TenantType.Demo;
    tenantData.ClientOrganizationType = TenantOrganizationType.CreditUnion;
    tenantData.IsActive = true;
    tenantData.IsMigratedClient = this.tenantCreationForm.controls.isMigratedClient.value;
    this.tenantService.addTenant(tenantData).subscribe({
      next: () => this.onSaveTenantComplete(TenantValidationErrors.RequestAccepted),
      error: (err) => this.onsSaveTenantError(err)
    });
    return;
  }

  private onSaveTenantComplete(response: string) {
    if (!(response = TenantValidationErrors.RequestAccepted)) {
      return;
    }
    this.tenantCreationForm.markAsPristine();
    this.backToTenantGrid();
  }

  private onsSaveTenantError(error: any) {
    console.log(error);
  }

  private SubscribeToClientCreation() {
    if (this.loggedInUserInfo != null) {
      this._subscribeToCreateClientStatus(this.loggedInUserInfo.externalUserId);
    }
  }

  private _subscribeToCreateClientStatus(createdExternalUserId: string) {
    this.store.dispatch(CoreActions.subscribeToCreateClientStatus({
      payload: {
        CreatedExternalUserId: createdExternalUserId
      }
    }));
  }

  getLoggedUser() {
    this.store.select(getLoggedInUserInfo).subscribe((user) => {
      if (user != null) {
        this.loggedInUserInfo = user;
      }
    });
  }

  /*
  * Check for unsaved changes in tenant creation form
  */
  hasUnsavedData(): boolean {
    return this.tenantCreationForm.dirty;
  }

  /*
  * Check for unsaved changes in tenant creation form
  */
  public isDirty(): boolean {
    return this.tenantCreationForm.dirty;
  }

  /*
  *  Validate whether the used client code value has already used on focus out
  */
  onFocusOutClientCode() {
    if (this.tenantCreationForm.controls.clientCode.value) {
      this.tenantService.validateField('TenantCode', this.tenantCreationForm.controls.clientCode.value)
        .subscribe((response: TenantValidationResponse) => {
          if (response.errors.length > 0) {
            if (response.errors.includes(TenantValidationErrors.ClientCode)) {
              this.clientCodeAlreadyUsed = true;
              this.databaseName = TenantConfigurationConstants.DatabaseNamePrefix;
            }
          } else {
            this.clientCodeAlreadyUsed = false;
            this.databaseName = `${TenantConfigurationConstants.DatabaseNamePrefix}${this.tenantCreationForm.controls.clientCode.value}`;
          }
        });
    }
  }

  /*
  * Validate whether the used client name value has already used on focus out
  */
  onFocusOutClientName() {
    if (this.tenantCreationForm.controls.clientName.value.trim()) {
      this.tenantService.validateField('TenantName', this.tenantCreationForm.controls.clientName.value.trim())
        .subscribe((response: TenantValidationResponse) => {
          if (response.errors.length > 0) {
            if (response.errors.includes(TenantValidationErrors.ClientName)) {
              this.clientNameAlreadyUsed = true;
            }
          } else {
            this.clientNameAlreadyUsed = false;
          }
        });
    }
  }

  /*
  * Validate whether the used solution id value has already used on focus out
  */
  onFocusOutSolutionId() {
    if (this.tenantCreationForm.controls.clientSolutionId.value) {
      this.tenantService.validateField('SolutionID', this.tenantCreationForm.controls.clientSolutionId.value)
        .subscribe((response: TenantValidationResponse) => {
          if (response.errors.length > 0) {
            if (response.errors.includes(TenantValidationErrors.SolutionId)) {
              this.solutionIdAlreadyUsed = true;
            }
          } else {
            this.solutionIdAlreadyUsed = false;
          }
        });
    }
  }

  /*
  * Validate whether the used default name domain value has already used on focus out
  */
  onFocusOutDomainName() {
    if (this.tenantCreationForm.controls.domainName.value.trim()) {
      this.identityService.domainNameExists(this.tenantCreationForm.controls.domainName.value.trim())
        .subscribe(
          (result) => {
            this.defaultDomainNameAlreadyUsed = result;
          });
    }
  }

  onChangeClientCode() {
    if (!this.tenantCreationForm.controls.clientCode.value) {
      this.databaseName = TenantConfigurationConstants.DatabaseNamePrefix
    }
    this.clientCodeAlreadyUsed = false;
  }

  onChangeClientName() {
    this.clientNameAlreadyUsed = false;
  }

  onChangeClientSolutionId() {
    this.solutionIdAlreadyUsed = false;
  }

  onChangeDomainName() {
    this.defaultDomainNameAlreadyUsed = false;
  }

  backToTenantGrid() {
    this.router.navigate(['admin/tenant']);
  }

  handleError(err: any, errorMessage: string) {
    console.log(err);
    //this.showErrorToast(errorMessage);
  }

  checkForDirty() {
    this.tenantCreationForm.valueChanges.subscribe(value => {
      this.dirtyEmitter.emit(true);
    });
  }

  omit_special_char(event: KeyboardEvent) {
    return !((event?.key == " ") || (event?.key == "<") || (event?.key == ">") || (event?.key == "&"));
  }

  /**
 * Cleans up the pasted input for a specific form control by removing forbidden characters.
 * @param {string} formControlName - The name of the form control to clean.
 */
  onPasteInputChange(formControlName: string) {
    setTimeout(() => {
      let domainName = this.tenantCreationForm.controls[formControlName].value;
      const forbiddenChars = ['<', '>', '&', ' '];
      for (const char of forbiddenChars) {
        domainName = domainName.split(char).join('');
      }
      domainName = domainName.replace(/[\x00-\x08\x0E-\x1F\x7F-\uFFFF]/g, '');
      this.tenantCreationForm.controls[formControlName].setValue(domainName);
    }, 0);
  }

  /**
 * Checks and updates the network status for saving a tenant. Listens to 'online' and 'offline' events.
 */
  checkNetworkStatusForSaveTenant() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')).pipe(map(() => navigator.onLine)).subscribe(status => {
        console.log('Network_status', status);
        this.networkStatus = status;
      });
  }

  /**
 * Updates the 'isMigratedClient' value in the tenant creation form based on the checkbox event.
 * @param {Event} checkedEvent - The checkbox click event.
 */
  OnMigratedClientClick(checkedEvent: Event) {

    const checked = (checkedEvent.target as HTMLInputElement).checked;

    if (checked) {
      this.tenantCreationForm.controls.isMigratedClient.setValue(true);
    } else {
      this.tenantCreationForm.controls.isMigratedClient.setValue(false);
    }
  }
}
